'use client';

import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  framework: 'pci-dss' | 'iso27001' | 'soc2' | 'operational';
  sections: string[];
  estimatedPages: number;
  lastGenerated: string;
  formats: ('pdf' | 'csv' | 'json')[];
  icon: string;
}

const templates: ReportTemplate[] = [
  { id: 'RPT-T-001', name: 'PCI-DSS Requirement 10 — Audit Log Report', description: 'Comprehensive audit trail report covering all access to cardholder data and system components as required by PCI-DSS Req 10.', framework: 'pci-dss', sections: ['Log Integrity', 'Access Events', 'Admin Actions', 'Failed Auths', 'Anomalies'], estimatedPages: 28, lastGenerated: '2026-03-15 06:00 UTC', formats: ['pdf', 'csv'], icon: 'DocumentTextIcon' },
  { id: 'RPT-T-002', name: 'PCI-DSS Vulnerability Scan Summary', description: 'Summary of detected vulnerabilities against cardholder data environment assets with remediation status.', framework: 'pci-dss', sections: ['CDE Asset Inventory', 'Open CVEs', 'Patch Status', 'Risk Scores'], estimatedPages: 14, lastGenerated: '2026-03-10 08:00 UTC', formats: ['pdf'], icon: 'ShieldExclamationIcon' },
  { id: 'RPT-T-003', name: 'ISO 27001 — Security Incident Register', description: 'Formal incident register aligned with ISO 27001 Annex A.16 requirements. Includes root cause analysis fields.', framework: 'iso27001', sections: ['Incident Log', 'Classification', 'Impact Assessment', 'Root Cause', 'Corrective Actions'], estimatedPages: 22, lastGenerated: '2026-03-16 07:00 UTC', formats: ['pdf', 'csv'], icon: 'ClipboardDocumentListIcon' },
  { id: 'RPT-T-004', name: 'ISO 27001 — ISMS Performance Metrics', description: 'KPI report for Information Security Management System performance review. Covers MTTD, MTTR, false positive rates.', framework: 'iso27001', sections: ['MTTD Trend', 'MTTR Trend', 'Alert Volume', 'FP Rate', 'SLA Compliance'], estimatedPages: 18, lastGenerated: '2026-03-01 00:00 UTC', formats: ['pdf'], icon: 'ChartBarIcon' },
  { id: 'RPT-T-005', name: 'SOC 2 Type II — Security Monitoring Evidence', description: 'Evidence package for SOC 2 Type II audit covering continuous monitoring controls and exception reporting.', framework: 'soc2', sections: ['Monitoring Coverage', 'Alert Handling', 'Incident Response', 'Access Reviews', 'Change Log'], estimatedPages: 45, lastGenerated: '2026-03-01 00:00 UTC', formats: ['pdf', 'csv', 'json'], icon: 'DocumentCheckIcon' },
  { id: 'RPT-T-006', name: 'SOC 2 — Availability & Confidentiality Controls', description: 'Controls report for Availability and Confidentiality trust service criteria with event evidence.', framework: 'soc2', sections: ['Uptime Metrics', 'Data Access Controls', 'Encryption Status', 'DLP Events'], estimatedPages: 31, lastGenerated: '2026-03-01 00:00 UTC', formats: ['pdf'], icon: 'LockClosedIcon' },
  { id: 'RPT-T-007', name: 'Daily Threat Summary', description: 'Automated daily digest of top threats, active incidents, blocked attacks, and analyst actions.', framework: 'operational', sections: ['Threat Score Trend', 'Top Attacks', 'Active Incidents', 'Blocked IPs', 'Analyst Actions'], estimatedPages: 6, lastGenerated: '2026-03-16 06:00 UTC', formats: ['pdf', 'csv'], icon: 'BoltIcon' },
  { id: 'RPT-T-008', name: 'Weekly SOC Operations Report', description: 'Weekly operational summary for management — incident trends, MTTD/MTTR, top attack vectors, asset health.', framework: 'operational', sections: ['Executive Summary', 'Incident Trends', 'Top Threats', 'Asset Health', 'Recommendations'], estimatedPages: 12, lastGenerated: '2026-03-09 08:00 UTC', formats: ['pdf'], icon: 'PresentationChartBarIcon' },
  { id: 'RPT-T-009', name: 'Asset Risk Posture Report', description: 'Risk score breakdown across all monitored assets with alert history, open vulnerabilities, and prioritized remediation list.', framework: 'operational', sections: ['Risk Score Distribution', 'High-Risk Assets', 'Open Alerts per Asset', 'Remediation Priority'], estimatedPages: 20, lastGenerated: '2026-03-15 00:00 UTC', formats: ['pdf', 'csv', 'json'], icon: 'ServerStackIcon' },
  { id: 'RPT-T-010', name: 'MITRE ATT&CK Coverage Map', description: 'Visual and tabular coverage map showing which MITRE ATT&CK techniques are covered by current detection rules.', framework: 'operational', sections: ['Tactic Coverage', 'Technique Gaps', 'Rule-to-Technique Mapping', 'Coverage Score'], estimatedPages: 16, lastGenerated: '2026-03-08 00:00 UTC', formats: ['pdf', 'json'], icon: 'MapIcon' },
  { id: 'RPT-T-011', name: 'Alert Rule Performance Report', description: 'False positive rate analysis, trigger frequency, and tuning recommendations for all active detection rules.', framework: 'operational', sections: ['Rule Trigger Stats', 'FP Rate Analysis', 'Suppression Effectiveness', 'Tuning Recommendations'], estimatedPages: 14, lastGenerated: '2026-03-10 00:00 UTC', formats: ['pdf', 'csv'], icon: 'AdjustmentsHorizontalIcon' },
  { id: 'RPT-T-012', name: 'Network Traffic Anomaly Report', description: 'Statistical analysis of network traffic patterns with anomaly detection results and baseline deviation metrics.', framework: 'operational', sections: ['Traffic Baselines', 'Anomaly Events', 'Protocol Distribution', 'Top Talkers', 'Geo Analysis'], estimatedPages: 19, lastGenerated: '2026-03-14 00:00 UTC', formats: ['pdf', 'csv'], icon: 'SignalIcon' },
];

const frameworkConfig: Record<string, { label: string; color: string; bg: string; border: string }> = {
  'pci-dss': { label: 'PCI-DSS', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  'iso27001': { label: 'ISO 27001', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  'soc2': { label: 'SOC 2', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  'operational': { label: 'Operational', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
};

const formatConfig: Record<string, { label: string; color: string }> = {
  pdf: { label: 'PDF', color: 'text-red-400' },
  csv: { label: 'CSV', color: 'text-emerald-400' },
  json: { label: 'JSON', color: 'text-blue-400' },
};

interface ReportTemplatesProps {
  frameworkFilter: string;
}

export default function ReportTemplates({ frameworkFilter }: ReportTemplatesProps) {
  const filtered = templates.filter((t) => frameworkFilter === 'all' || t.framework === frameworkFilter);

  const handleGenerate = (template: ReportTemplate, format: string) => {
    // Backend integration: POST /api/reports/generate { templateId: template.id, format, dateRange: ... }
    toast.success(`Generating ${template.name} as ${format.toUpperCase()} — check Report History when ready`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
      {filtered.map((template) => {
        const fwCfg = frameworkConfig[template.framework];
        return (
          <div
            key={template.id}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex flex-col gap-4 hover:border-zinc-700 transition-colors group"
          >
            {/* Header */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <Icon name={template.icon as any} size={16} className="text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${fwCfg.color} ${fwCfg.bg} ${fwCfg.border}`}>
                    {fwCfg.label}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-zinc-200 leading-snug">{template.name}</h3>
              </div>
            </div>

            {/* Description */}
            <p className="text-xs text-zinc-500 leading-relaxed flex-1">{template.description}</p>

            {/* Sections */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Sections</p>
              <div className="flex flex-wrap gap-1.5">
                {template.sections.map((s) => (
                  <span key={s} className="text-[10px] bg-zinc-800 border border-zinc-700 text-zinc-500 px-1.5 py-0.5 rounded">
                    {s}
                  </span>
                ))}
              </div>
            </div>

            {/* Meta */}
            <div className="flex items-center justify-between text-[11px] text-zinc-600 pt-2 border-t border-zinc-800">
              <span className="font-mono">~{template.estimatedPages} pages</span>
              <span>Last: {template.lastGenerated.split(' ')[0]}</span>
            </div>

            {/* Generate Buttons */}
            <div className="flex items-center gap-2">
              {template.formats.map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => handleGenerate(template, fmt)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-zinc-600 rounded-lg transition-colors active:scale-95"
                >
                  <Icon name="ArrowDownTrayIcon" size={11} className={formatConfig[fmt].color} />
                  <span className={`text-[11px] font-semibold ${formatConfig[fmt].color}`}>{fmt.toUpperCase()}</span>
                </button>
              ))}
              <button
                onClick={() => toast.success(`${template.name} added to schedule`)}
                className="p-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-zinc-600 rounded-lg transition-colors"
                title="Schedule this report"
              >
                <Icon name="ClockIcon" size={13} className="text-zinc-500" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}